import React, {FC, FormEvent, useCallback, useEffect, useMemo, useState} from "react";
import {
    ComboBox,
    DefaultButton,
    Dropdown,
    IComboBoxOption,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Stack,
    Text,
    TextField,
} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {AiSupportRunBudgetMode, CompanyAiSupportSettingsDto} from "../API";

export interface ICompanyAiSupportSettingsLike {
    aiSupportApiKey?: string | null;
    aiSupportTriageModel?: string | null;
    aiSupportInvestigationModel?: string | null;
    aiSupportReasoningEffort?: string | null;
    aiSupportVerbosity?: string | null;
    aiSupportRunBudgetMode?: AiSupportRunBudgetMode | null;
    aiSupportMaxToolRounds?: number | null;
    aiSupportMaxInputTokensPerTurn?: number | null;
    aiSupportMaxOutputTokensPerTurn?: number | null;
    aiSupportDefaultTimeoutMinutes?: number | null;
}

interface ICompanyAiSupportSettingsFormProps {
    settings?: ICompanyAiSupportSettingsLike;
    save: (value: CompanyAiSupportSettingsDto) => Promise<unknown> | unknown;
    loadModels?: (apiKey?: string) => Promise<string[]>;
}

const RECOMMENDED_DEFAULTS = {
    maxToolRounds: 10,
    maxInputTokensPerTurn: 12000,
    maxOutputTokensPerTurn: 16000,
    defaultTimeoutMinutes: 8,
};

const toNumberString = (value?: number | null) =>
    value === undefined || value === null ? "" : value.toString();

const isLegacyRunUntilTerminalSettings = (
    settings?: ICompanyAiSupportSettingsLike,
) => settings?.aiSupportRunBudgetMode === AiSupportRunBudgetMode.RunUntilTerminal;

export const normalizeCompanyAiSupportSettingsForForm = (
    settings?: ICompanyAiSupportSettingsLike,
) => {
    const useLegacyBoundedDefaults = isLegacyRunUntilTerminalSettings(settings);

    return {
        apiKey: settings?.aiSupportApiKey ?? "",
        triageModel: settings?.aiSupportTriageModel ?? "",
        investigationModel: settings?.aiSupportInvestigationModel ?? "",
        reasoningEffort: settings?.aiSupportReasoningEffort ?? "medium",
        verbosity: settings?.aiSupportVerbosity ?? "concise",
        runBudgetMode: AiSupportRunBudgetMode.Bounded,
        maxToolRounds: toNumberString(
            useLegacyBoundedDefaults
                ? RECOMMENDED_DEFAULTS.maxToolRounds
                : settings?.aiSupportMaxToolRounds ??
                      RECOMMENDED_DEFAULTS.maxToolRounds,
        ),
        maxInputTokensPerTurn: toNumberString(
            settings?.aiSupportMaxInputTokensPerTurn ??
                RECOMMENDED_DEFAULTS.maxInputTokensPerTurn,
        ),
        maxOutputTokensPerTurn: toNumberString(
            settings?.aiSupportMaxOutputTokensPerTurn ??
                RECOMMENDED_DEFAULTS.maxOutputTokensPerTurn,
        ),
        defaultTimeoutMinutes: toNumberString(
            useLegacyBoundedDefaults
                ? RECOMMENDED_DEFAULTS.defaultTimeoutMinutes
                : settings?.aiSupportDefaultTimeoutMinutes ??
                      RECOMMENDED_DEFAULTS.defaultTimeoutMinutes,
        ),
    };
};

const parsePositiveInteger = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const CompanyAiSupportSettingsForm: FC<ICompanyAiSupportSettingsFormProps> = ({
    settings,
    save,
    loadModels,
}) => {
    const {t} = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [triageModel, setTriageModel] = useState("");
    const [investigationModel, setInvestigationModel] = useState("");
    const [reasoningEffort, setReasoningEffort] = useState("medium");
    const [verbosity, setVerbosity] = useState("concise");
    const [runBudgetMode, setRunBudgetMode] = useState(AiSupportRunBudgetMode.Bounded);
    const [maxToolRounds, setMaxToolRounds] = useState("");
    const [maxInputTokensPerTurn, setMaxInputTokensPerTurn] = useState("");
    const [maxOutputTokensPerTurn, setMaxOutputTokensPerTurn] = useState("");
    const [defaultTimeoutMinutes, setDefaultTimeoutMinutes] = useState("");
    const [saving, setSaving] = useState(false);
    const [availableModels, setAvailableModels] = useState<string[]>([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [modelsLoadAttempted, setModelsLoadAttempted] = useState(false);
    const [modelsError, setModelsError] = useState<string>();
    const normalizedSettings = useMemo(
        () => normalizeCompanyAiSupportSettingsForForm(settings),
        [settings],
    );

    useEffect(() => {
        setApiKey(normalizedSettings.apiKey);
        setTriageModel(normalizedSettings.triageModel);
        setInvestigationModel(normalizedSettings.investigationModel);
        setReasoningEffort(normalizedSettings.reasoningEffort);
        setVerbosity(normalizedSettings.verbosity);
        setRunBudgetMode(normalizedSettings.runBudgetMode);
        setMaxToolRounds(normalizedSettings.maxToolRounds);
        setMaxInputTokensPerTurn(normalizedSettings.maxInputTokensPerTurn);
        setMaxOutputTokensPerTurn(normalizedSettings.maxOutputTokensPerTurn);
        setDefaultTimeoutMinutes(normalizedSettings.defaultTimeoutMinutes);
    }, [normalizedSettings]);

    const reasoningOptions = useMemo<IDropdownOption[]>(
        () => [
            {key: "minimal", text: t("ai-support-reasoning-effort-minimal")},
            {key: "low", text: t("ai-support-reasoning-effort-low")},
            {key: "medium", text: t("ai-support-reasoning-effort-medium")},
            {key: "high", text: t("ai-support-reasoning-effort-high")},
            {key: "none", text: t("ai-support-reasoning-effort-none")},
        ],
        [t],
    );

    const verbosityOptions = useMemo<IDropdownOption[]>(
        () => [
            {key: "auto", text: t("ai-support-verbosity-auto")},
            {key: "concise", text: t("ai-support-verbosity-concise")},
            {key: "detailed", text: t("ai-support-verbosity-detailed")},
        ],
        [t],
    );

    const modelOptions = useMemo<IComboBoxOption[]>(
        () =>
            availableModels.map((model) => ({
                key: model,
                text: model,
            })),
        [availableModels],
    );

    const canLoadModels = !!loadModels && apiKey.trim().length > 0;

    useEffect(() => {
        if (!canLoadModels) {
            setAvailableModels([]);
            setLoadingModels(false);
            setModelsLoadAttempted(false);
            setModelsError(undefined);
            return;
        }

        let cancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setLoadingModels(true);
            setModelsError(undefined);
            try {
                const models = await loadModels?.(apiKey.trim());
                if (cancelled) {
                    return;
                }

                setAvailableModels(models ?? []);
                setModelsLoadAttempted(true);
            } catch (error: any) {
                if (cancelled) {
                    return;
                }

                setAvailableModels([]);
                setModelsLoadAttempted(true);
                setModelsError(
                    error?.response?.data ??
                        error?.message ??
                        t("ai-support-models-load-failed-generic"),
                );
            } finally {
                if (!cancelled) {
                    setLoadingModels(false);
                }
            }
        }, 500);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [apiKey, canLoadModels, loadModels, t]);

    const parsedNumbers = useMemo(
        () => ({
            maxToolRounds: parsePositiveInteger(maxToolRounds),
            maxInputTokensPerTurn: parsePositiveInteger(maxInputTokensPerTurn),
            maxOutputTokensPerTurn: parsePositiveInteger(maxOutputTokensPerTurn),
            defaultTimeoutMinutes: parsePositiveInteger(defaultTimeoutMinutes),
        }),
        [
            defaultTimeoutMinutes,
            maxInputTokensPerTurn,
            maxOutputTokensPerTurn,
            maxToolRounds,
        ],
    );

    const validationErrors = useMemo(() => {
        const errors: string[] = [];

        if (!triageModel.trim()) {
            errors.push(t("ai-support-triage-model-required"));
        }

        if (!investigationModel.trim()) {
            errors.push(t("ai-support-investigation-model-required"));
        }

        if (!reasoningEffort.trim()) {
            errors.push(t("ai-support-reasoning-effort-required"));
        }

        if (!verbosity.trim()) {
            errors.push(t("ai-support-verbosity-required"));
        }

        if (!parsedNumbers.maxToolRounds) {
            errors.push(t("ai-support-max-tool-rounds-required"));
        }

        if (!parsedNumbers.maxInputTokensPerTurn) {
            errors.push(t("ai-support-max-input-tokens-required"));
        }

        if (!parsedNumbers.maxOutputTokensPerTurn) {
            errors.push(t("ai-support-max-output-tokens-required"));
        }

        if (!parsedNumbers.defaultTimeoutMinutes) {
            errors.push(t("ai-support-timeout-minutes-required"));
        }

        return errors;
    }, [
        investigationModel,
        parsedNumbers.defaultTimeoutMinutes,
        parsedNumbers.maxInputTokensPerTurn,
        parsedNumbers.maxOutputTokensPerTurn,
        parsedNumbers.maxToolRounds,
        reasoningEffort,
        t,
        triageModel,
        verbosity,
    ]);

    const warnings = useMemo(() => {
        const nextWarnings: string[] = [];

        if (
            parsedNumbers.maxToolRounds &&
            parsedNumbers.maxToolRounds > RECOMMENDED_DEFAULTS.maxToolRounds
        ) {
            nextWarnings.push(
                t("ai-support-settings-warning-over-default", {
                    field: t("ai-support-max-tool-rounds"),
                    current: parsedNumbers.maxToolRounds,
                    recommended: RECOMMENDED_DEFAULTS.maxToolRounds,
                }),
            );
        }

        if (
            parsedNumbers.maxInputTokensPerTurn &&
            parsedNumbers.maxInputTokensPerTurn >
                RECOMMENDED_DEFAULTS.maxInputTokensPerTurn
        ) {
            nextWarnings.push(
                t("ai-support-settings-warning-over-default", {
                    field: t("ai-support-max-input-tokens"),
                    current: parsedNumbers.maxInputTokensPerTurn,
                    recommended: RECOMMENDED_DEFAULTS.maxInputTokensPerTurn,
                }),
            );
        }

        if (
            parsedNumbers.maxOutputTokensPerTurn &&
            parsedNumbers.maxOutputTokensPerTurn >
                RECOMMENDED_DEFAULTS.maxOutputTokensPerTurn
        ) {
            nextWarnings.push(
                t("ai-support-settings-warning-over-default", {
                    field: t("ai-support-max-output-tokens"),
                    current: parsedNumbers.maxOutputTokensPerTurn,
                    recommended: RECOMMENDED_DEFAULTS.maxOutputTokensPerTurn,
                }),
            );
        }

        if (parsedNumbers.maxOutputTokensPerTurn && parsedNumbers.maxOutputTokensPerTurn < 8000) {
            nextWarnings.push(t("ai-support-output-budget-warning"));
        }

        if (
            parsedNumbers.defaultTimeoutMinutes &&
            parsedNumbers.defaultTimeoutMinutes >
                RECOMMENDED_DEFAULTS.defaultTimeoutMinutes
        ) {
            nextWarnings.push(
                t("ai-support-settings-warning-over-default", {
                    field: t("ai-support-timeout-minutes"),
                    current: parsedNumbers.defaultTimeoutMinutes,
                    recommended: RECOMMENDED_DEFAULTS.defaultTimeoutMinutes,
                }),
            );
        }

        return nextWarnings;
    }, [
        parsedNumbers.defaultTimeoutMinutes,
        parsedNumbers.maxInputTokensPerTurn,
        parsedNumbers.maxOutputTokensPerTurn,
        parsedNumbers.maxToolRounds,
        t,
    ]);

    const changed = useMemo(
        () =>
            (settings?.aiSupportApiKey ?? "") !== apiKey ||
            (settings?.aiSupportTriageModel ?? "") !== triageModel ||
            (settings?.aiSupportInvestigationModel ?? "") !== investigationModel ||
            (settings?.aiSupportReasoningEffort ?? "medium") !== reasoningEffort ||
            (settings?.aiSupportVerbosity ?? "concise") !== verbosity ||
            (settings?.aiSupportRunBudgetMode ?? AiSupportRunBudgetMode.Bounded) !== runBudgetMode ||
            toNumberString(settings?.aiSupportMaxToolRounds) !== maxToolRounds ||
            toNumberString(settings?.aiSupportMaxInputTokensPerTurn) !==
                maxInputTokensPerTurn ||
            toNumberString(settings?.aiSupportMaxOutputTokensPerTurn) !==
                maxOutputTokensPerTurn ||
            toNumberString(settings?.aiSupportDefaultTimeoutMinutes) !==
                defaultTimeoutMinutes,
        [
            apiKey,
            defaultTimeoutMinutes,
            investigationModel,
            maxInputTokensPerTurn,
            maxOutputTokensPerTurn,
            maxToolRounds,
            runBudgetMode,
            reasoningEffort,
            settings,
            triageModel,
            verbosity,
        ],
    );

    const submit = useCallback(
        async (event?: FormEvent<HTMLFormElement>) => {
            if (event) {
                event.preventDefault();
            }

            if (validationErrors.length > 0) {
                return;
            }

            setSaving(true);
            try {
                await Promise.resolve(
                    save({
                        aiSupportApiKey: apiKey.trim() || undefined,
                        aiSupportTriageModel: triageModel.trim(),
                        aiSupportInvestigationModel: investigationModel.trim(),
                        aiSupportReasoningEffort: reasoningEffort.trim(),
                        aiSupportVerbosity: verbosity.trim(),
                        aiSupportRunBudgetMode: runBudgetMode,
                        aiSupportMaxToolRounds: parsedNumbers.maxToolRounds ?? undefined,
                        aiSupportMaxInputTokensPerTurn:
                            parsedNumbers.maxInputTokensPerTurn ?? undefined,
                        aiSupportMaxOutputTokensPerTurn:
                            parsedNumbers.maxOutputTokensPerTurn ?? undefined,
                        aiSupportDefaultTimeoutMinutes:
                            parsedNumbers.defaultTimeoutMinutes ?? undefined,
                    }),
                );
            } finally {
                setSaving(false);
            }
        },
        [
            apiKey,
            investigationModel,
            parsedNumbers.defaultTimeoutMinutes,
            parsedNumbers.maxInputTokensPerTurn,
            parsedNumbers.maxOutputTokensPerTurn,
            parsedNumbers.maxToolRounds,
            runBudgetMode,
            reasoningEffort,
            save,
            triageModel,
            validationErrors.length,
            verbosity,
        ],
    );

    return (
        <form onSubmit={submit}>
            <Stack
                tokens={{childrenGap: 12}}
                style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    padding: 16,
                    background: "white",
                    maxWidth: 960,
                }}
            >
                <Stack tokens={{childrenGap: 4}}>
                    <Text style={{fontWeight: 600}}>
                        {t("ai-support-settings-tab")}
                    </Text>
                    <Text variant={"small"}>
                        {t("ai-support-settings-description")}
                    </Text>
                </Stack>

                <MessageBar messageBarType={MessageBarType.info}>
                    {t("ai-support-api-key-fallback-note")}
                </MessageBar>

                <MessageBar messageBarType={MessageBarType.info}>
                    {t("ai-support-settings-starting-point", {
                        maxToolRounds: RECOMMENDED_DEFAULTS.maxToolRounds,
                        maxInputTokensPerTurn:
                            RECOMMENDED_DEFAULTS.maxInputTokensPerTurn,
                        maxOutputTokensPerTurn:
                            RECOMMENDED_DEFAULTS.maxOutputTokensPerTurn,
                        defaultTimeoutMinutes:
                            RECOMMENDED_DEFAULTS.defaultTimeoutMinutes,
                    })}
                </MessageBar>

                {loadingModels && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        {t("ai-support-models-loading")}
                    </MessageBar>
                )}

                {!loadingModels && !!modelsError && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        {t("ai-support-models-load-failed", {error: modelsError})}
                    </MessageBar>
                )}

                {!loadingModels && !modelsError && canLoadModels && availableModels.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.success}>
                        {t("ai-support-models-loaded", {count: availableModels.length})}
                    </MessageBar>
                )}

                {!loadingModels &&
                    !modelsError &&
                    canLoadModels &&
                    modelsLoadAttempted &&
                    availableModels.length === 0 && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            {t("ai-support-models-empty")}
                        </MessageBar>
                    )}

                {validationErrors.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        <Stack tokens={{childrenGap: 4}}>
                            {validationErrors.map((error) => (
                                <Text key={error} variant={"small"}>
                                    {error}
                                </Text>
                            ))}
                        </Stack>
                    </MessageBar>
                )}

                {warnings.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        <Stack tokens={{childrenGap: 4}}>
                            {warnings.map((warning) => (
                                <Text key={warning} variant={"small"}>
                                    {warning}
                                </Text>
                            ))}
                        </Stack>
                    </MessageBar>
                )}

                <Stack horizontal wrap tokens={{childrenGap: 12}}>
                    <TextField
                        label={t("ai-support-api-key")}
                        type={"password"}
                        value={apiKey}
                        onChange={(_, value) => setApiKey(value ?? "")}
                        placeholder={t("ai-support-api-key-placeholder")}
                        description={t("ai-support-api-key-description")}
                        styles={{root: {minWidth: 320}}}
                    />
                    {canLoadModels ? (
                        <>
                            <ComboBox
                                label={t("ai-support-triage-model")}
                                selectedKey={triageModel || undefined}
                                text={triageModel}
                                options={modelOptions}
                                allowFreeform
                                autoComplete={"on"}
                                onChange={(_, option, __, value) =>
                                    setTriageModel(value ?? option?.key?.toString() ?? "")
                                }
                                placeholder={t("ai-support-triage-model-placeholder")}
                                useComboBoxAsMenuWidth
                                styles={{root: {minWidth: 320}}}
                            />
                            <ComboBox
                                label={t("ai-support-investigation-model")}
                                selectedKey={investigationModel || undefined}
                                text={investigationModel}
                                options={modelOptions}
                                allowFreeform
                                autoComplete={"on"}
                                onChange={(_, option, __, value) =>
                                    setInvestigationModel(
                                        value ?? option?.key?.toString() ?? "",
                                    )
                                }
                                placeholder={t("ai-support-investigation-model-placeholder")}
                                useComboBoxAsMenuWidth
                                styles={{root: {minWidth: 320}}}
                            />
                        </>
                    ) : (
                        <>
                            <TextField
                                label={t("ai-support-triage-model")}
                                value={triageModel}
                                onChange={(_, value) => setTriageModel(value ?? "")}
                                placeholder={t("ai-support-triage-model-placeholder")}
                                description={t("ai-support-triage-model-description")}
                                styles={{root: {minWidth: 320}}}
                            />
                            <TextField
                                label={t("ai-support-investigation-model")}
                                value={investigationModel}
                                onChange={(_, value) =>
                                    setInvestigationModel(value ?? "")
                                }
                                placeholder={t(
                                    "ai-support-investigation-model-placeholder",
                                )}
                                description={t(
                                    "ai-support-investigation-model-description",
                                )}
                                styles={{root: {minWidth: 320}}}
                            />
                        </>
                    )}
                </Stack>

                <Stack horizontal wrap tokens={{childrenGap: 12}}>
                    <Dropdown
                        label={t("ai-support-reasoning-effort")}
                        selectedKey={reasoningEffort}
                        options={reasoningOptions}
                        onChange={(_, option) =>
                            setReasoningEffort(option?.key?.toString() ?? "medium")
                        }
                        styles={{dropdown: {minWidth: 220}}}
                    />
                    <Dropdown
                        label={t("ai-support-verbosity")}
                        title={t("ai-support-verbosity-description")}
                        selectedKey={verbosity}
                        options={verbosityOptions}
                        onChange={(_, option) =>
                            setVerbosity(option?.key?.toString() ?? "concise")
                        }
                        styles={{dropdown: {minWidth: 220}}}
                    />
                </Stack>

                <Stack tokens={{childrenGap: 4}}>
                    <Text style={{fontWeight: 600}}>
                        {t("ai-support-run-budget-mode")}: {t("ai-support-run-budget-mode-bounded")}
                    </Text>
                    <Text variant={"small"} style={{color: "#605e5c"}}>
                        {t("ai-support-run-budget-mode-bounded-note")}
                    </Text>
                    <Text variant={"small"} style={{color: "#605e5c"}}>
                        {t("ai-support-input-budget-description")} {t("ai-support-output-budget-description")} {t("ai-support-verbosity-description")}
                    </Text>
                </Stack>

                <Stack horizontal wrap tokens={{childrenGap: 12}}>
                    <TextField
                        label={t("ai-support-max-tool-rounds")}
                        type={"number"}
                        value={maxToolRounds}
                        onChange={(_, value) => setMaxToolRounds(value ?? "")}
                        styles={{root: {minWidth: 200}}}
                    />
                    <TextField
                        label={t("ai-support-max-input-tokens")}
                        title={t("ai-support-input-budget-description")}
                        type={"number"}
                        value={maxInputTokensPerTurn}
                        onChange={(_, value) => setMaxInputTokensPerTurn(value ?? "")}
                        styles={{root: {minWidth: 220}}}
                    />
                    <TextField
                        label={t("ai-support-max-output-tokens")}
                        title={t("ai-support-output-budget-description")}
                        type={"number"}
                        value={maxOutputTokensPerTurn}
                        onChange={(_, value) => setMaxOutputTokensPerTurn(value ?? "")}
                        styles={{root: {minWidth: 220}}}
                    />
                    <TextField
                        label={t("ai-support-timeout-minutes")}
                        type={"number"}
                        value={defaultTimeoutMinutes}
                        onChange={(_, value) => setDefaultTimeoutMinutes(value ?? "")}
                        styles={{root: {minWidth: 200}}}
                    />
                </Stack>

                <Stack horizontal tokens={{childrenGap: 8}}>
                    <PrimaryButton
                        text={t("save")}
                        onClick={() => submit()}
                        disabled={!changed || validationErrors.length > 0 || saving}
                    />
                    <DefaultButton
                        text={t("reset")}
                        onClick={() => {
                            setApiKey(normalizedSettings.apiKey);
                            setTriageModel(normalizedSettings.triageModel);
                            setInvestigationModel(normalizedSettings.investigationModel);
                            setReasoningEffort(normalizedSettings.reasoningEffort);
                            setVerbosity(normalizedSettings.verbosity);
                            setRunBudgetMode(normalizedSettings.runBudgetMode);
                            setMaxToolRounds(normalizedSettings.maxToolRounds);
                            setMaxInputTokensPerTurn(normalizedSettings.maxInputTokensPerTurn);
                            setMaxOutputTokensPerTurn(normalizedSettings.maxOutputTokensPerTurn);
                            setDefaultTimeoutMinutes(normalizedSettings.defaultTimeoutMinutes);
                        }}
                        disabled={!changed || saving}
                    />
                </Stack>
            </Stack>
        </form>
    );
};
